import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./CustomFields.css";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  CustomField,
  CustomFieldService,
  CustomFieldRequest,
} from "./customFields.service";
import CustomFieldForm from "./CustomFieldForm";
import { useCustomFields } from "./useCustomFields";
import {
  showConfirmationAlert,
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { AuthContext } from "../../../context/authcontext";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import SortableHeader from "../../../components/filters/SortableHeader";
import { getAuthHeaders } from "../../../utils/authUtils";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import FieldConfigurationSidebar from "../../../components/Tabs/FieldConfigurationSidebar";
import Tooltip from "@mui/material/Tooltip";

const MySwal = withReactContent(Swal);

const CustomFields = () => {
  const { selectedTenantId, auth, lifecycleTypeNames } = useContext(AuthContext);

  const TAB_OPTIONS = useMemo(() => [
    { value: "company", label: "Company", icon: "ti ti-building-community" },
    { value: "contact", label: "Contact", icon: "ti ti-user-up" },
    { value: "lifecycle", label: "Lifecycle", icon: "ti ti-chart-arcs" },
    {
      value: "contract",
      label: getLifecycleTypeName("CONTRACT"),
      icon: "ti ti-file-certificate",
    },
  ], []);

  const selectedTenant = auth?.tenants?.find(
    (t: any) => String(t.tenant_id) === String(selectedTenantId)
  );
  const userRole = selectedTenant?.role_key || selectedTenant?.role_name || "";
  const isSalesUser = userRole === "SALESUSER";

  const {
    customFields,
    loading,
    backgroundLoading,
    error,
    dataTypes,
    dataTypesLoading,
    addCustomField,
    updateCustomField,
    removeCustomField,
    fetchCustomFields,
    fetchDataTypes,
    isInitialized,
    clearBackgroundLoading,
    clearCustomFieldsData,
  } = useCustomFields();

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingCustomField, setIsLoadingCustomField] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CustomField | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [entityModalKey, setEntityModalKey] = useState(0);

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "display_order",
    sortOrder: "asc",
  });

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [entityFilter, setEntityFilter] = useState<string>("company");
  const [activeTab, setActiveTab] = useState<string>("company");
  const [isDragging, setIsDragging] = useState(false);

  useTenantChange({
    onTenantChange: () => {
      clearCustomFieldsData();
      setSelectedItems([]);
      setSelectAll(false);
      setEntityFilter("company");
      setSelectedEntities([]);
      setActiveTab("company");
    },
    clearData: clearCustomFieldsData,
  });

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "custom_field_display_name",
      showDateFilter: false,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      showStatusFilter: false,
      defaultSort: {
        field: "display_order",
        order: "asc" as const,
      },
    }),
    []
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      const filtersWithSort = {
        ...newFilters,
        sortBy: newFilters.sortBy || "display_order",
        sortOrder: newFilters.sortOrder || "asc",
      };
      setFilters(filtersWithSort);
      fetchCustomFields({
        ...filtersWithSort,
        entityFilter,
      });
    },
    [fetchCustomFields, entityFilter]
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setEntityFilter(tab);
      const sortBy = "display_order";
      const sortOrder: "asc" | "desc" = "asc";
      const newFilters = { ...filters, sortBy, sortOrder };
      setFilters(newFilters);
      fetchCustomFields({
        ...newFilters,
        entityFilter: tab,
      });
    },
    [filters, fetchCustomFields]
  );

  const handleFiltersReset = useCallback(() => {
    const resetFilters = {
      searchTerm: "",
      dateRange: [null, null] as [Date | null, Date | null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "display_order",
      sortOrder: "asc" as const,
    };

    setFilters(resetFilters);
    setEntityFilter("company");
    setActiveTab("company");
    fetchCustomFields({
      ...resetFilters,
      entityFilter: "company",
    });
  }, [fetchCustomFields]);


  const handleSortChange = useCallback(
    (newSortBy: string, newOrder: "asc" | "desc") => {
      const newFilters = {
        ...filters,
        sortBy: newSortBy,
        sortOrder: newOrder,
      };
      setFilters(newFilters);
    },
    [filters]
  );

  const displayedItems = useMemo(() => {
    if (!Array.isArray(customFields)) {
      return [];
    }
    
    let dataToSort = [...customFields];
    
    if (filters.sortBy) {
      dataToSort.sort((a: any, b: any) => {
        let aValue = a[filters.sortBy];
        let bValue = b[filters.sortBy];
        
        if (filters.sortBy === 'custom_field_data_type') {
          aValue = a.custom_field_data_type?.custom_field_data_type_display_name?.toLowerCase() || "";
          bValue = b.custom_field_data_type?.custom_field_data_type_display_name?.toLowerCase() || "";
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue?.toLowerCase() || "";
        }
        
        if (aValue < bValue) return filters.sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return filters.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return dataToSort;
  }, [customFields, filters.sortBy, filters.sortOrder]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const validIds = displayedItems
        .filter((item) => item.custom_field_id)
        .map((item) => item.custom_field_id as number);
      setSelectedItems(validIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id: number) => {
    setSelectedItems((prev) => {
      const newSelected = prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id];

      const validCurrentData = displayedItems.filter(
        (item) => item.custom_field_id
      );
      setSelectAll(
        newSelected.length === validCurrentData.length &&
        validCurrentData.length > 0
      );
      return newSelected;
    });
  };

  useEffect(() => {
    if (displayedItems.length > 0) {
      const validCurrentData = displayedItems.filter(
        (item) => item.custom_field_id
      );
      const allCurrentSelected = validCurrentData.every(
        (item) =>
          item.custom_field_id && selectedItems.includes(item.custom_field_id)
      );
      setSelectAll(allCurrentSelected && selectedItems.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [displayedItems, selectedItems]);

  useEffect(() => {
    if (!isInitialized && selectedTenantId) {
      fetchCustomFields({
        sortBy: "display_order",
        sortOrder: "asc",
        entityFilter,
      });
    }
  }, [isInitialized, fetchCustomFields, selectedTenantId, entityFilter]);

  const handleEntitySelection = (entities: string[]) => {
    setShowEntityModal(false);
    setSelectedEntities(entities);
    if (entities.length === 1) {
      fetchDataTypes();
      setShowCreateModal(true);
    } else if (entities.length > 1) {
      fetchDataTypes();
      setShowBulkCreateModal(true);
    }
  };

  const handleCreate = async (formData: Partial<CustomFieldRequest>, localEntities?: string[]) => {
    setIsCreating(true);
    try {
      const entitiesToCreate = localEntities || (formData.related_entity_name ? [formData.related_entity_name] : selectedEntities);

      if (entitiesToCreate.length === 1) {
        const nextDisplayOrder = await CustomFieldService.getNextDisplayOrder(
          selectedTenantId!,
          formData.related_entity_name || entitiesToCreate[0]
        );

        const payloadWithUserInfo = {
          ...formData,
          display_order: nextDisplayOrder,
          created_by: auth?.user_id,
          updated_by: auth?.user_id,
        };

        const newItem = await CustomFieldService.createCustomField(
          payloadWithUserInfo,
          selectedTenantId!
        );

        if (newItem.related_entity_name === entityFilter) {
          addCustomField(newItem);
        }

        setSelectedItem(null);
        setShowCreateModal(false);
        setShowBulkCreateModal(false);
        setSelectedEntities([]);

        const filtersWithSort = {
          ...filters,
          sortBy: filters.sortBy || "display_order",
          sortOrder: filters.sortOrder || "asc",
        };
        fetchCustomFields({
          ...filtersWithSort,
          entityFilter,
        });

        setTimeout(() => {
          showSuccessAlert(
            `Custom field "${newItem.custom_field_display_name}" created successfully!`
          );
        }, 500);
      } else {
        await handleBulkCreate(formData, entitiesToCreate);
      }
    } catch (err) {
      console.error("Failed to create custom field:", err);
      showErrorAlert("Failed to create custom field. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleBulkCreate = async (formData: Partial<CustomFieldRequest>, localEntities?: string[]) => {
    setIsCreating(true);
    try {
      const entitiesToCreate = localEntities || selectedEntities;
      const fieldsToCreate = await Promise.all(
        entitiesToCreate.map(async (entity) => {
          const nextDisplayOrder = await CustomFieldService.getNextDisplayOrder(
            selectedTenantId!,
            entity
          );
          return {
            ...formData,
            related_entity_name: entity,
            display_order: nextDisplayOrder,
            created_by: auth?.user_id,
            updated_by: auth?.user_id,
          };
        })
      );

      const newItems = await CustomFieldService.createMultipleCustomFields(
        fieldsToCreate,
        selectedTenantId!
      );

      newItems.forEach((item) => {
        if (item.related_entity_name === entityFilter) {
          addCustomField(item);
        }
      });

      setShowCreateModal(false);
      setShowBulkCreateModal(false);
      setSelectedEntities([]);

      const filtersWithSort = {
        ...filters,
        sortBy: filters.sortBy || "display_order",
        sortOrder: filters.sortOrder || "asc",
      };
      fetchCustomFields({
        ...filtersWithSort,
        entityFilter,
      });

      setTimeout(() => {
        showSuccessAlert(
          `Custom field "${formData.custom_field_display_name}" created for ${entitiesToCreate.length} entities!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to create custom fields:", err);
      showErrorAlert("Failed to create custom fields. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };


  const handleUpdate = async (
    id: number,
    formData: Partial<CustomFieldRequest>
  ) => {
    setIsUpdating(true);
    try {
      const { choice_options, ...customFieldData } = formData;
      const payloadWithUserInfo = {
        ...customFieldData,
        updated_by: auth?.user_id,
      };
      const updatedItem = await CustomFieldService.updateCustomField(
        id,
        payloadWithUserInfo,
        selectedTenantId!
      );

      if (choice_options && choice_options.length > 0) {
        for (const option of choice_options) {
          if (option.custom_field_choice_option_id) {
            await CustomFieldService.updateChoiceOption(
              option.custom_field_choice_option_id,
              {
                choice_label: option.choice_label!,
              },
              selectedTenantId!
            );
          } else {
            await CustomFieldService.createChoiceOption(
              {
                choice_label: option.choice_label!,
                display_order: option.display_order!,
                custom_field_id: id,
                tenant_id: Number(selectedTenantId!),
              },
              selectedTenantId!
            );
          }
        }
      }

      updateCustomField(updatedItem);
      setSelectedItem(null);

      setTimeout(() => {
        showSuccessAlert(
          `Custom field "${updatedItem.custom_field_display_name}" updated successfully!`
        );
      }, 500);
    } catch (err) {
      console.error("Failed to update custom field:", err);
      showErrorAlert("Failed to update custom field. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyFieldKey = async (item: CustomField) => {
    try {
      const entityName = item.related_entity_name.toLowerCase();
      const fieldName = item.custom_field_display_name
        .toLowerCase()
        .replace(/\s+/g, '_');
      const fieldKey = `${entityName}_${fieldName}`;

      await navigator.clipboard.writeText(fieldKey);
      showSuccessAlert(`Custom field key "${fieldKey}" copied to clipboard!`);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      showErrorAlert("Failed to copy to clipboard. Please try again.");
    }
  };

  const handleDelete = async (item: CustomField) => {
    const result = await showConfirmationAlert(
      "Delete Custom Field",
      `Are you sure you want to delete "${item.custom_field_display_name}"? This action cannot be undone.`
    );

    if (result.isConfirmed) {
      try {
        if (item.custom_field_id) {
          await CustomFieldService.deleteCustomField(item.custom_field_id);
          removeCustomField(item.custom_field_id);

          const remainingItems = customFields
            .filter(cf => cf.custom_field_id !== item.custom_field_id && cf.related_entity_name === entityFilter)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

          if (remainingItems.length > 0) {
            const updates = remainingItems.map((field, index) => ({
              custom_field_id: field.custom_field_id!,
              display_order: index + 1,
            }));

            await CustomFieldService.updateDisplayOrders(updates, selectedTenantId!);
          }

          const filtersWithSort = {
            ...filters,
            sortBy: "display_order",
            sortOrder: "asc" as const,
          };
          setFilters(filtersWithSort);
          fetchCustomFields({
            ...filtersWithSort,
            entityFilter,
          });

          await showSuccessAlert(
            `Custom field "${item.custom_field_display_name}" deleted successfully!`
          );
        }
      } catch (err) {
        console.error("Failed to delete custom field:", err);
        await showErrorAlert(
          "Failed to delete custom field. Please try again."
        );
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedItems.map((id) =>
        CustomFieldService.deleteCustomField(id)
      );

      await Promise.all(deletePromises);

      selectedItems.forEach((id) => removeCustomField(id));

      const remainingItems = customFields
        .filter(cf => !selectedItems.includes(cf.custom_field_id!) && cf.related_entity_name === entityFilter)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

      if (remainingItems.length > 0) {
        const updates = remainingItems.map((field, index) => ({
          custom_field_id: field.custom_field_id!,
          display_order: index + 1,
        }));

        await CustomFieldService.updateDisplayOrders(updates, selectedTenantId!);
      }

      setSelectedItems([]);
      setSelectAll(false);
      setShowBulkDeleteModal(false);

      const filtersWithSort = {
        ...filters,
        sortBy: "display_order",
        sortOrder: "asc" as const,
      };
      setFilters(filtersWithSort);
      fetchCustomFields({
        ...filtersWithSort,
        entityFilter,
      });

      await showSuccessAlert(
        `${selectedItems.length} custom field${selectedItems.length > 1 ? "s" : ""
        } deleted successfully!`
      );
    } catch (err) {
      console.error("Failed to delete custom fields:", err);
      await showErrorAlert(
        "Failed to delete some custom fields. Please try again."
      );
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    setIsDragging(false);

    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const items = Array.from(displayedItems);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    const updates = items.map((item, index) => ({
      custom_field_id: item.custom_field_id!,
      display_order: index + 1,
    }));

    try {
      await CustomFieldService.updateDisplayOrders(updates, selectedTenantId!);

      items.forEach((item, index) => {
        item.display_order = index + 1;
      });

      updateCustomField(reorderedItem);

      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Display order updated successfully!",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      const filtersWithSort = {
        ...filters,
        sortBy: "display_order",
        sortOrder: "asc" as const,
      };
      setFilters(filtersWithSort);
      fetchCustomFields({
        ...filtersWithSort,
        entityFilter,
      });
    } catch (error) {
      console.error("Error updating display order:", error);
      MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to update display order",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const columns = [
    {
      title: (
        <div className="text-center">
          <i
            className="ti ti-grip-vertical text-muted"
            title="Drag to reorder"
          />
        </div>
      ),
      dataIndex: "dragHandle",
      width: "50px",
      align: "center",
      className: "drag-handle-column",
      render: (text: string, record: CustomField, index: number) => (
        <div className="drag-handle text-center">
          <i
            className="ti ti-grip-vertical text-muted cursor-grab"
            title="Drag to reorder"
          />
        </div>
      ),
    },
    ...(isSalesUser
      ? []
      : [
        {
          title: (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id="select-all"
                checked={!!selectAll}
                onChange={handleSelectAll}
              />
            </div>
          ),
          dataIndex: "checkbox",
          width: "50px",
          render: (text: string, record: CustomField) => (
            <div className="form-check form-check-md">
              <input
                className="form-check-input"
                type="checkbox"
                id={`select-${record.custom_field_id}`}
                checked={
                  record.custom_field_id
                    ? selectedItems.includes(record.custom_field_id)
                    : false
                }
                onChange={() =>
                  record.custom_field_id &&
                  handleSelectItem(record.custom_field_id)
                }
              />
            </div>
          ),
        },
      ]),
    {
      title: (
        <SortableHeader
          title="Field Name"
          field="custom_field_display_name"
          currentSort={filters.sortBy}
          currentOrder={filters.sortOrder}
          onSort={handleSortChange}
        />
      ),
      dataIndex: "custom_field_display_name",
      render: (name: string, record: CustomField) => {
        const isTruncated = record.custom_field_display_name.length > 20;
        const displayName = isTruncated
          ? `${record.custom_field_display_name.substring(0, 20)}...`
          : record.custom_field_display_name;

        return (
          <div>
            {isTruncated ? (
              <Tooltip title={record.custom_field_display_name} placement="top" arrow>
                <div className="fw-semibold" style={{ cursor: 'default' }}>
                  {displayName}
                </div>
              </Tooltip>
            ) : (
              <div className="fw-semibold" style={{ cursor: 'default' }}>
                {displayName}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "custom_field_data_type",
      render: (type: any, record: CustomField) => (
        <span className="badge badge-soft-info">
          {record.custom_field_data_type.custom_field_data_type_display_name}
        </span>
      ),
    },
    {
      title: "Required",
      dataIndex: "is_required",
      render: (isRequired: boolean) => (
        <span
          className={`badge ${isRequired ? "badge-soft-warning" : "badge-soft-primary"
            }`}
        >
          {isRequired ? "Required" : "Optional"}
        </span>
      ),
    },
    {
      title: (
        <div className="text-center">
          <SortableHeader
            title="Display Order"
            field="display_order"
            currentSort={filters.sortBy}
            currentOrder={filters.sortOrder}
            onSort={handleSortChange}
          />
        </div>
      ),
      dataIndex: "display_order",
      align: "center",
      render: (displayOrder: number) => (
        <div className="text-center">
          <span className="badge badge-soft-primary display-order-badge">
            {displayOrder || 0}
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (isActive: boolean) => (
        <span
          className={`badge ${isActive ? "badge-soft-success" : "badge-soft-danger"
            }`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    ...(isSalesUser
      ? []
      : [
        {
          title: "Actions",
          dataIndex: "actions",
          render: (text: string, record: CustomField) => (
            <div className="dropdown table-action">
              <Link
                to="#"
                className="action-icon"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
              >
                <i className="fa fa-ellipsis-v" />
              </Link>
              <div className="dropdown-menu dropdown-menu-end">

                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => {
                    setSelectedItem(record);
                    fetchDataTypes();

                    if (record.custom_field_id) {
                      const loadFullCustomField = async () => {
                        setIsLoadingCustomField(true);
                        try {
                          const fullCustomField =
                            await CustomFieldService.getCustomFieldById(
                              record.custom_field_id!
                            );

                          const isChoiceType =
                            fullCustomField.custom_field_data_type &&
                            [
                              "DROPDOWN",
                              "RADIO_OPTION",
                              "MULTIPLE_OPTION",
                            ].includes(
                              fullCustomField.custom_field_data_type
                                .custom_field_data_type_key
                            );

                          if (isChoiceType) {
                            const choiceOptions =
                              await CustomFieldService.getChoiceOptionsByCustomFieldId(
                                record.custom_field_id!
                              );
                            fullCustomField.choice_options =
                              choiceOptions.sort(
                                (a, b) =>
                                  (a.display_order || 0) -
                                  (b.display_order || 0)
                              );
                          }

                          setSelectedItem(fullCustomField);
                        } catch (error) {
                          console.error(
                            "Error fetching custom field details:",
                            error
                          );
                        } finally {
                          setIsLoadingCustomField(false);
                        }
                      };

                      loadFullCustomField();
                    }
                  }}
                >
                  <i className="ti ti-edit text-blue"></i> Edit
                </Link>
                {(record.related_entity_name === "company" ||
                  record.related_entity_name === "contact") && (
                    <Link
                      className="dropdown-item"
                      to="#"
                      onClick={() => handleCopyFieldKey(record)}
                    >
                      <i className="ti ti-copy text-success"></i> Copy Custom Field Key
                    </Link>
                  )}
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleDelete(record)}
                >
                  <i className="ti ti-trash text-danger"></i> Delete
                </Link>
              </div>
            </div>
          ),
        },
      ]),
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="field-configuration"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>

            <div className="row">
              <FieldConfigurationSidebar />

              <div className="col-xl-9 col-lg-12">
                <div className="card" style={{ position: "relative" }}>
                  {loading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255)",
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "not-allowed",
                      }}
                    >
                      <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading Custom Fields
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <h4 className="fw-semibold mb-0">Custom Fields</h4>
                      </div>
                      <div className="col-sm-8">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          {!isSalesUser && selectedItems.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-danger ms-3"
                              onClick={() => setShowBulkDeleteModal(true)}
                              disabled={isBulkDeleting}
                            >
                              <i className="ti ti-trash me-2" />
                              Delete Selected ({selectedItems.length})
                            </button>
                          )}
                          {!isSalesUser && (
                            <button
                              className="btn btn-primary ms-3"
                              onClick={() => {
                                setSelectedEntities([]);
                                setEntityModalKey(prev => prev + 1);
                                setShowEntityModal(true);
                              }}
                            >
                              <i className="ti ti-square-rounded-plus me-2" />
                              Add Custom Field
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <TableFilters
                        config={filterConfig}
                        data={customFields}
                        onFilterChange={handleFilterChange}
                        onApiFilterChange={() => { }}
                        onApiCall={() => Promise.resolve()}
                        onFiltersReset={handleFiltersReset}
                        tenantId={selectedTenantId || undefined}
                        className="mt-4"
                      />
                    </div>
                  </div>

                  <div className="card-body pt-0">
                    <div className="d-flex flex-wrap gap-2 mb-3 justify-content-center">
                      {TAB_OPTIONS.map((tab) => (
                        <button
                          key={tab.value}
                          className={`btn btn-sm d-flex align-items-center px-3 py-2 rounded-pill transition-all ${activeTab === tab.value
                            ? "btn-primary text-white shadow-sm"
                            : "btn-outline-light text-muted border-light-subtle hover-bg-light"
                            }`}
                          onClick={() => handleTabChange(tab.value)}
                          type="button"
                          disabled={backgroundLoading}
                          style={{
                            minWidth: "fit-content",
                            fontSize: "0.875rem",
                            fontWeight: activeTab === tab.value ? "600" : "500",
                            transform:
                              activeTab === tab.value ? "translateY(-1px)" : "none",
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {backgroundLoading && activeTab === tab.value ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                                style={{ width: "0.75rem", height: "0.75rem" }}
                              ></span>
                              {tab.label}
                            </>
                          ) : (
                            <>
                              <i
                                className={`${tab.icon} me-2`}
                                style={{ fontSize: "1rem" }}
                              ></i>
                              {tab.label}
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {loading ? (
                    <div className="card-body">
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading Custom Fields...
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="card-body">
                      <div className="alert alert-danger text-center">
                        <i className="ti ti-alert-circle me-2"></i>
                        {error}
                      </div>
                    </div>
                  ) : (
                    <div className="card-body">
                      <div
                        className="table-responsive contact-table-wrapper"
                        style={{ position: "relative" }}
                      >
                        {backgroundLoading && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                              zIndex: 999,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: "200px",
                            }}
                          >
                            <div className="text-center">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading</span>
                              </div>
                              <div className="mt-2">
                                <small className="text-muted">
                                  Loading {TAB_OPTIONS.find(tab => tab.value === activeTab)?.label} Custom Fields...
                                </small>
                              </div>
                            </div>
                          </div>
                        )}
                        {displayedItems.length === 0 ? (
                          <div className="text-center py-5">
                            <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                              <i className="ti ti-forms fs-36 text-gray"></i>
                            </div>
                            <h4 className="text-gray mb-2">
                              No Custom Fields Found!
                            </h4>
                            <p className="text-muted mb-3">
                              Create custom fields to capture additional information
                              for your entities.
                            </p>
                            {!isSalesUser && (
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  setSelectedEntities([]);
                                  setEntityModalKey(prev => prev + 1);
                                  setShowEntityModal(true);
                                }}
                              >
                                <i className="fa fa-plus me-2" />
                                Create Your First Custom Field
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <DragDropContext
                              onDragEnd={handleDragEnd}
                              onDragStart={handleDragStart}
                            >
                              <Droppable droppableId="custom-fields-table" isDropDisabled={isSalesUser}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`custom-fields-droppable ${snapshot.isDraggingOver ? "dragging-over" : ""
                                      }`}
                                  >
                                    <table className="table table-striped contact-table custom-fields-table" style={{ minWidth: "100%", whiteSpace: "normal" }}>
                                      <thead>
                                        <tr>
                                          {columns.map((column: any, index) => (
                                            <th
                                              key={index}
                                              style={{ width: column.width }}
                                            >
                                              {column.title}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {displayedItems.map((record, index) => (
                                          <Draggable
                                            key={record.custom_field_id || index}
                                            draggableId={`field-${record.custom_field_id || index
                                              }`}
                                            index={index}
                                            isDragDisabled={isSalesUser}
                                          >
                                            {(provided, snapshot) => (
                                              <tr
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={`${snapshot.isDragging
                                                  ? "dragging"
                                                  : ""
                                                  } ${isDragging ? "drag-active" : ""
                                                  }`}
                                                style={{
                                                  ...provided.draggableProps.style,
                                                  backgroundColor:
                                                    snapshot.isDragging
                                                      ? "#f8f9fa"
                                                      : "transparent",
                                                }}
                                              >
                                                {columns.map(
                                                  (column: any, colIndex) => (
                                                    <td
                                                      key={colIndex}
                                                      style={{
                                                        width: column.width,
                                                      }}
                                                    >
                                                      {colIndex === 0 ? (
                                                        <div
                                                          {...provided.dragHandleProps}
                                                        >
                                                          {column.render
                                                            ? column.render(
                                                              "",
                                                              record,
                                                              index
                                                            )
                                                            : ""}
                                                        </div>
                                                      ) : column.render ? (
                                                        column.render(
                                                          column.dataIndex
                                                            ? (record as any)[
                                                            column.dataIndex
                                                            ]
                                                            : "",
                                                          record,
                                                          index
                                                        )
                                                      ) : column.dataIndex ? (
                                                        (record as any)[
                                                        column.dataIndex
                                                        ]
                                                      ) : (
                                                        ""
                                                      )}
                                                    </td>
                                                  )
                                                )}
                                              </tr>
                                            )}
                                          </Draggable>
                                        ))}
                                        {provided.placeholder}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showEntityModal}
        onHide={() => {
          setShowEntityModal(false);
          setSelectedEntities([]);
        }}
        size="lg"
        key={`entity-modal-${entityModalKey}`}
        enforceFocus={false}
        restoreFocus={false}
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Entities for Custom Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">
            Select one or more entities where you want to add the custom field.
            You can create the same field for multiple entities at once.
          </p>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="selectAllEntities"
                checked={selectedEntities.length === TAB_OPTIONS.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedEntities(
                      TAB_OPTIONS.map((entity) => entity.value)
                    );
                  } else {
                    setSelectedEntities([]);
                  }
                }}
              />
              <label
                className="form-check-label fw-semibold"
                htmlFor="selectAllEntities"
              >
                Select All Entities
              </label>
            </div>
            <small className="text-muted">
              {selectedEntities.length} of {TAB_OPTIONS.length} selected
            </small>
          </div>
          <div className="row">
            {TAB_OPTIONS.map((entity) => (
              <div key={entity.value} className="col-md-6 col-lg-6 mb-3">
                <div
                  className={`card entity-card cursor-pointer ${selectedEntities.includes(entity.value)
                    ? "border-primary bg-light-primary"
                    : ""
                    }`}
                  onClick={() => {
                    setSelectedEntities((prev) =>
                      prev.includes(entity.value)
                        ? prev.filter((e) => e !== entity.value)
                        : [...prev, entity.value]
                    );
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-body text-center p-3">
                    <i className={`${entity.icon} fs-24 mb-2 text-primary`}></i>
                    <h6 className="mb-0">{entity.label}</h6>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              setShowEntityModal(false);
              setSelectedEntities([]);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleEntitySelection(selectedEntities)}
            disabled={selectedEntities.length === 0}
          >
            Continue ({selectedEntities.length} selected)
          </button>
        </Modal.Footer>
      </Modal>

      {!isSalesUser && (
        <>
          <Modal
            show={showCreateModal || showBulkCreateModal}
            onHide={() => {
              setShowCreateModal(false);
              setShowBulkCreateModal(false);
              setSelectedEntities([]);
            }}
            size="xl"
            className="custom-field-modal"
            enforceFocus={false}
            restoreFocus={false}
            animation={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedEntities.length > 1
                  ? "Create Custom Field for Multiple Entities"
                  : "Add New Custom Field"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CustomFieldForm
                key={`create-form-${selectedEntities.join('-')}`}
                dataTypes={dataTypes}
                selectedEntity={selectedEntities.length === 1 ? selectedEntities[0] : ""}
                selectedEntities={selectedEntities}
                onSubmit={handleCreate}
                onCancel={() => {
                  setShowCreateModal(false);
                  setShowBulkCreateModal(false);
                  setSelectedEntities([]);
                }}
                isSubmitting={isCreating}
                dataTypesLoading={dataTypesLoading}
              />
            </Modal.Body>
          </Modal>

          <Modal
            show={
              selectedItem !== null && !showCreateModal && !showBulkCreateModal
            }
            onHide={() => setSelectedItem(null)}
            size="xl"
            className="custom-field-modal"
            enforceFocus={false}
            restoreFocus={false}
            animation={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Edit Custom Field</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedItem && (
                <CustomFieldForm
                  customField={selectedItem}
                  dataTypes={dataTypes}
                  selectedEntity={selectedItem.related_entity_name}
                  selectedEntities={[selectedItem.related_entity_name]}
                  onSubmit={(formData) => {
                    if (selectedItem.custom_field_id) {
                      return handleUpdate(
                        selectedItem.custom_field_id,
                        formData
                      );
                    }
                    return Promise.resolve();
                  }}
                  onCancel={() => {
                    setSelectedItem(null);
                  }}
                  isSubmitting={isUpdating}
                  dataTypesLoading={dataTypesLoading}
                  isLoadingCustomField={isLoadingCustomField}
                />
              )}
            </Modal.Body>
          </Modal>

          <Modal
            show={showBulkDeleteModal}
            onHide={() => setShowBulkDeleteModal(false)}
            enforceFocus={false}
            restoreFocus={false}
            animation={false}
          >
            <Modal.Header closeButton>
              <Modal.Title>Delete Selected Custom Fields</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Delete Selected Custom Fields?</h4>
                <p className="mb-0">
                  Are you sure you want to delete {selectedItems.length}{" "}
                  selected{" "}
                  {selectedItems.length > 1 ? "custom fields" : "custom field"}?
                  <br />
                  This action cannot be undone.
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Deleting...
                  </>
                ) : (
                  `Yes, Delete ${selectedItems.length > 1 ? "All" : "It"}`
                )}
              </button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default CustomFields;
